import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppDateInput from '../../../components/AppDateInput'
import AppDropdown from '../../../components/AppDropdown'
import AppCombobox from '../../../components/AppCombobox'
import AppScrollArea from '../../../components/AppScrollArea'
import AppToggle from '../../../components/AppToggle'
import { useTaskEntryScreen } from './hooks/useTaskEntryScreen'
import { fetchTaskOptionsPage, fetchTaskUserOptionsPage } from './api/taskApi'
import { useInfiniteOptions } from '../../../hooks/useInfiniteOptions'
import { appToast } from '../../../components/AppToast'
import AllocationAssignmentModal from '../../allocation/components/AllocationAssignmentModal'
import { useRefreshEmployeeAvailabilityMutation } from '../../../api/serverStateHooks'
import { AppTooltip } from '../../../components/AppTooltip'

function initials(name) {
  return String(name || 'U')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

function statusChipClass(status) {
  if (status === 'IN_PROGRESS') return 'border-primary bg-primary-fixed text-primary'
  if (status === 'COMPLETED') return 'border-emerald-300 bg-emerald-100 text-emerald-700'
  if (status === 'CLOSED') return 'border-outline-variant bg-surface-container-high text-on-surface-variant'
  return 'border-outline-variant bg-surface-container-low text-on-surface-variant'
}

function riskToneClass(level) {
  if (level === 'High') return 'text-error'
  if (level === 'Low') return 'text-emerald-700'
  return 'text-amber-600'
}

export default function TaskConfigurationScreen() {
  const navigate = useNavigate()
  const { state, actions } = useTaskEntryScreen()
  const formAssignedToCsv = Array.isArray(state?.form?.assigned_to_csv) ? state.form.assigned_to_csv : []
  const formDependencyUserIds = Array.isArray(state?.form?.dependency_user_ids) ? state.form.dependency_user_ids : []
  const formDependencyTaskIds = Array.isArray(state?.form?.dependency_task_ids) ? state.form.dependency_task_ids : []
  const priorities = Array.isArray(state?.priorities) ? state.priorities : []

  useEffect(() => {
    if (state.error) {
      appToast.error(state.error)
    }
  }, [state.error])

  useEffect(() => {
    if (state.success) {
      appToast.success(state.success)
    }
  }, [state.success])

  useEffect(() => {
    if (state.optionError) {
      appToast.warning(state.optionError)
    }
  }, [state.optionError])

  const [isAssigneePickerOpen, setIsAssigneePickerOpen] = useState(false)
  const [isDependencyPickerOpen, setIsDependencyPickerOpen] = useState(false)
  const [availabilityUser, setAvailabilityUser] = useState(null)
  const [refreshEmployee, { isLoading: isRefreshingEmployee }] = useRefreshEmployeeAvailabilityMutation()

  function getCurrentMonth() {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  }

  const {
    items: rawAssigneeOptions,
    search: assigneeSearch,
    setSearch: setAssigneeSearch,
    isLoading: assigneeIsLoading,
    hasMore: assigneeHasMore,
    sentinelRef: assigneeSentinelRef,
  } = useInfiniteOptions({
    fetchOptions: fetchTaskUserOptionsPage,
    enabled: isAssigneePickerOpen,
  })

  const userNameById = useMemo(() => {
    const map = new Map()
      ; (Array.isArray(state.userOptions) ? state.userOptions : []).forEach((user) => {
        map.set(String(user.id), String(user.name || '').trim())
      })
      ; (Array.isArray(rawAssigneeOptions) ? rawAssigneeOptions : []).forEach((user) => {
        map.set(String(user.value), String(user.label || '').trim())
      })
    return map
  }, [rawAssigneeOptions, state.userOptions])

  const {
    items: rawDependencyTasks,
    search: depSearch,
    setSearch: setDepSearch,
    isLoading: depIsLoading,
    hasMore: depHasMore,
    sentinelRef: depSentinelRef,
  } = useInfiniteOptions({
    fetchOptions: fetchTaskOptionsPage,
    enabled: isDependencyPickerOpen,
  })
  const safeAssigneeOptions = Array.isArray(rawAssigneeOptions) ? rawAssigneeOptions : []
  const safeDependencyTasks = Array.isArray(rawDependencyTasks) ? rawDependencyTasks : []

  // We map the already selected assigned_to_csv ids to objects.
  // Since we removed userOptions, we just rely on what's available or map them generically.
  // For simplicity, we just use their IDs if full data isn't loaded yet.
  const assignedUsers = useMemo(
    () =>
      formAssignedToCsv
        .map((id) => {
          const name = userNameById.get(String(id))
          return { id, name: name || 'User' }
        })
        .filter(Boolean),
    [formAssignedToCsv, userNameById],
  )

  const responsibleUsers = useMemo(
    () =>
      formDependencyUserIds
        .map((id) => ({ id, name: userNameById.get(String(id)) || 'User' })),
    [formDependencyUserIds, userNameById],
  )

  const dependencyTasks = useMemo(
    () =>
      formDependencyTaskIds
        .map((id) => {
          const loaded = safeDependencyTasks.find((t) => Number(t.value) === Number(id))
          return loaded ? { id: loaded.value, task_name: loaded.label, task_code: loaded.subLabel } : { id, task_name: `Task #${id}` }
        }),
    [formDependencyTaskIds, safeDependencyTasks],
  )

  return (
    <div className="mx-auto w-full max-w-[1320px] pb-8">
      <header className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="mb-2 text-sm font-medium text-on-surface-variant">
            Tasks <span className="mx-2">&gt;</span>{' '}
            <span className="font-bold text-primary">{state.isEditMode ? 'Task Configuration' : 'New Task Entry'}</span>
          </p>
          <h1 className="text-4xl font-extrabold tracking-tight text-on-surface md:text-5xl">
            {state.isEditMode ? 'Update Task' : 'Create Task'}
          </h1>
        </div>
      </header>



      {state.isLoading ? (
        <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest px-6 py-16 text-center text-sm text-on-surface-variant">
          Loading task configuration...
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-7 xl:grid-cols-[1fr_280px]">
          <div className="space-y-7">
            <article className="rounded-[2rem] border border-outline-variant/20 bg-surface-container-low p-7">
              {/* <div className="mb-6 flex items-center gap-2 text-on-surface">
                <span className="material-symbols-outlined text-primary">info</span>
                <h2 className="text-3xl font-black tracking-tight">Core Information</h2>
              </div> */}

              <h3 className="mb-6 flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-on-surface-variant">
                <span className="material-symbols-outlined text-base text-primary">info</span>
                Core Information
              </h3>

              <div className="space-y-5">
                <label className="grid gap-2">
                  <span className="text-sm font-semibold text-on-surface">
                    Task Name <span className="text-error">*</span>
                  </span>
                  <input
                    type="text"
                    value={state.form.task_name}
                    onChange={(event) => actions.patchForm('task_name', event.target.value)}
                    placeholder="e.g. Q4 Financial Audit Migration"
                    className="rounded-xl border-none bg-surface-container-lowest px-4 py-3 text-base text-on-surface outline-none"
                  />
                </label>

                <div className="grid gap-2">
                  <span className="text-sm font-semibold text-on-surface">Task Type Template</span>
                  <div className="flex items-stretch gap-2">
                    <div className="flex-1">
                      <AppDropdown
                        value={state.form.task_master_id}
                        onChange={actions.handleTaskMasterChange}
                        disabled={state.isEditMode}
                        placeholder={state.isEditMode ? 'Blueprint template locked' : 'Select template blueprint (optional)'}
                        triggerClassName="rounded-xl border-none bg-surface-container-lowest px-4 py-3 text-base text-on-surface disabled:opacity-60"
                        options={[
                          { value: '', label: 'No Template (Manual)' },
                          ...state.taskMasterOptions.map((tm) => ({
                            value: String(tm.id),
                            label: `${tm.task_type_name} (${tm.task_type_code})`,
                          })),
                        ]}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate('/masters/task-master/entry')}
                      disabled={state.isEditMode}
                      title="Create New Task Type Template"
                      className="flex w-[48px] items-center justify-center rounded-xl bg-primary text-on-primary shadow-sm transition hover:bg-primary/90 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <span className="material-symbols-outlined font-bold">add</span>
                    </button>
                  </div>
                </div>
                {!state.isEditMode && state.form.task_master_id ? (
                  <label className="flex items-center justify-between gap-4 rounded-xl bg-surface-container-lowest px-4 py-3">
                    <span>
                      <span className="block text-sm font-bold text-on-surface">Create checklist jobs from template</span>
                      <span className="block text-xs text-on-surface-variant">Linked Job Master rows will be added after this task is saved.</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={Boolean(state.form.create_template_jobs)}
                      onChange={(event) => actions.patchForm('create_template_jobs', event.target.checked)}
                      className="h-5 w-5 accent-primary"
                    />
                  </label>
                ) : null}

                <div className="grid grid-cols-1 gap-5 md:grid-cols-[1fr_auto]">
                  <label className="grid gap-2">
                    <span className="text-sm font-semibold text-on-surface">
                      Project <span className="text-error">*</span>
                    </span>
                    <AppCombobox
                      value={state.form.project_id}
                      onChange={(value) => actions.patchForm('project_id', value)}
                      placeholder="Select project"
                      searchPlaceholder="Search projects..."
                      allowClear={false}
                      triggerClassName="rounded-xl border-none bg-surface-container-lowest px-4 py-3 text-base text-on-surface"
                      fetchOptions={actions.fetchProjectOptions}
                      selectedLabelOverride={state.initialProjectName}
                    />
                  </label>

                  <div className="grid gap-2">
                    <span className="text-sm font-semibold text-on-surface">Priority</span>
                    <div className="flex items-center gap-2">
                      {priorities.map((priority) => (
                        <button
                          key={priority}
                          type="button"
                          onClick={() => actions.setPriority(priority)}
                          className={`rounded-xl px-5 py-3 text-sm font-black uppercase tracking-[0.12em] transition-colors ${state.form.priority === priority
                            ? 'border-2 border-primary bg-surface-container-lowest text-primary'
                            : 'bg-surface-container-high text-on-surface-variant'
                            }`}
                        >
                          {priority}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <label className="grid gap-2">
                  <span className="text-sm font-semibold text-on-surface">Description</span>
                  <textarea
                    rows={4}
                    value={state.form.task_description}
                    onChange={(event) => actions.patchForm('task_description', event.target.value)}
                    placeholder="Describe the scope and technical requirements..."
                    className="resize-none rounded-xl border-none bg-surface-container-lowest px-4 py-3 text-base text-on-surface outline-none"
                  />
                </label>
              </div>
            </article>

            <article className="rounded-lg bg-surface-container-low p-8">
              <h3 className="mb-6 flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-on-surface-variant">
                <span className="material-symbols-outlined text-base text-primary">badge</span>
                Assignment
              </h3>

              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <div className="space-y-4">
                  <label className="block text-sm font-semibold text-on-surface-variant">
                    Internal Assignee <span className="text-error">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {assignedUsers.length === 0 ? (
                      <span className="flex items-center gap-2 rounded-full border border-dashed border-primary/50 bg-primary/5 px-3 py-1.5 text-primary">
                        <span className="material-symbols-outlined text-sm">inbox</span>
                        <span className="text-xs font-bold">Unassigned (Task Pool)</span>
                      </span>
                    ) : (
                      assignedUsers.map((user) => (
                        <span key={user.id} className="flex items-center gap-2 rounded-full border border-outline-variant/40 bg-surface-container-lowest px-3 py-2">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-fixed text-[10px] font-bold text-primary">
                            {initials(user.name)}
                          </span>
                          <span className="text-xs font-medium text-on-surface">{user.name}</span>
                          <AppTooltip content="View resource availability and assignment">
                            <button
                              type="button"
                              onClick={() => setAvailabilityUser(user)}
                              className="material-symbols-outlined text-xs text-on-surface-variant transition-colors hover:text-primary"
                              style={{ fontVariationSettings: "'FILL' 1" }}
                            >
                              schedule
                            </button>
                          </AppTooltip>
                          <button
                            type="button"
                            onClick={() => actions.removeAssignee(user.id)}
                            className="material-symbols-outlined text-xs text-on-surface-variant transition-colors hover:text-error"
                          >
                            close
                          </button>
                        </span>
                      ))
                    )}
                    <button
                      type="button"
                      onClick={() => setIsAssigneePickerOpen(true)}
                      className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-dashed border-outline-variant text-on-surface-variant transition-all hover:border-primary hover:text-primary"
                      title="Assign or change assignee"
                    >
                      <span className="material-symbols-outlined text-base">add</span>
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="block text-sm font-semibold text-on-surface-variant">Responsible Persons</label>
                  <AppCombobox
                    multiple={true}
                    value={formDependencyUserIds.map(String)}
                    onChange={(values) => {
                      // actions.addResponsibleUser doesn't accept an array, we patch form directly
                      actions.patchForm('dependency_user_ids', values.map(Number))
                    }}
                    fetchOptions={fetchTaskUserOptionsPage}
                    placeholder="Search stakeholders..."
                    triggerClassName="w-full rounded-xl border-none bg-surface-container-lowest px-4 py-3 text-base text-on-surface"
                  />
                  {responsibleUsers.length ? (
                    <div className="flex flex-wrap gap-2">
                      {responsibleUsers.map((user) => (
                        <span key={user.id} className="inline-flex items-center gap-2 rounded-full border border-outline-variant/40 bg-surface-container-lowest px-3 py-1.5">
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-tertiary-fixed text-[10px] font-black text-primary">
                            {initials(user.name)}
                          </span>
                          <span className="text-xs font-semibold text-on-surface">{user.name}</span>
                          <button
                            type="button"
                            onClick={() => actions.removeResponsibleUser(user.id)}
                            className="material-symbols-outlined text-sm text-on-surface-variant hover:text-error"
                          >
                            close
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </article>

            <article className="rounded-[2rem] border border-outline-variant/20 bg-surface-container-low p-7">
              {/* <div className="mb-6 flex items-center gap-2 text-on-surface">
                <span className="material-symbols-outlined text-primary">schedule</span>
                <h2 className="text-3xl font-black tracking-tight">Schedule &amp; SLA</h2>
              </div> */}

              <h3 className="mb-6 flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-on-surface-variant">
                <span className="material-symbols-outlined text-base text-primary">schedule</span>
                Schedule &amp; SLA
              </h3>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <label className="grid gap-2">
                  <span className="text-sm font-semibold text-on-surface">
                    Start Date <span className="text-error">*</span>
                  </span>
                  <AppDateInput
                    value={state.form.planned_start}
                    onChange={(value) => actions.patchForm('planned_start', value)}
                    inputClassName="rounded-xl border-none bg-surface-container-lowest px-4 py-3 text-base text-on-surface shadow-none"
                  />
                </label>

                <label className="grid gap-2">
                  <span className="text-sm font-semibold text-on-surface">
                    End Date <span className="text-error">*</span>
                  </span>
                  <AppDateInput
                    value={state.form.planned_end}
                    onChange={(value) => actions.patchForm('planned_end', value)}
                    inputClassName="rounded-xl border-none bg-surface-container-lowest px-4 py-3 text-base text-on-surface shadow-none"
                  />
                </label>

                {/* <label className="grid gap-2">
                  <span className="text-sm font-semibold text-on-surface">Status</span>
                  <div className="rounded-xl bg-surface-container-lowest px-4 py-3 text-base font-semibold text-on-surface">
                    {state.form.status.replaceAll('_', ' ')}
                  </div>
                </label> */}

                <div className="grid gap-2">
                  <span className="text-sm font-semibold text-on-surface">
                    Effort Estimate <span className="text-error">*</span>
                  </span>
                  <div className="grid grid-cols-2 gap-1">
                    <label className="grid gap-2">
                      <span className="text-xs font-semibold tracking-wide text-outline">
                        Hours
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={state.form.effort_hours}
                        onChange={(event) => actions.setEffortHours(event.target.value)}
                        placeholder="e.g. 4"
                        className="rounded-xl border-none bg-surface-container-lowest px-4 py-3 text-base text-on-surface outline-none"
                      />
                    </label>

                    <label className="grid gap-2">
                      <span className="text-xs font-semibold tracking-wide text-outline">
                        Minutes
                      </span>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        step="1"
                        value={state.form.effort_minutes}
                        onChange={(event) => actions.setEffortMinutes(event.target.value)}
                        placeholder="e.g. 30"
                        className="rounded-xl border-none bg-surface-container-lowest px-4 py-3 text-base text-on-surface outline-none"
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                <label className="grid gap-2">
                  <span className="text-sm font-semibold text-on-surface">
                    Completion Note
                    {['COMPLETED', 'CLOSED'].includes(String(state.form.status || '').toUpperCase()) ? (
                      <span className="text-error"> *</span>
                    ) : null}
                  </span>
                  <input
                    type="text"
                    value={state.form.completion_note}
                    onChange={(event) => actions.patchForm('completion_note', event.target.value)}
                    placeholder="Required when status is COMPLETED/CLOSED"
                    className="rounded-xl border-none bg-surface-container-lowest px-4 py-3 text-base text-on-surface outline-none"
                  />
                </label>

                <label className="grid gap-2">
                  <span className="text-sm font-semibold text-on-surface">Effort Estimate (hours)</span>
                  <input
                    type="number"
                    min="0"
                    value={state.form.effort_estimate}
                    onChange={(event) => actions.setEffortEstimate(event.target.value)}
                    className="rounded-xl border-none bg-surface-container-lowest px-4 py-3 text-base text-on-surface outline-none"
                  />
                </label>
              </div> */}
            </article>

            <article className="rounded-lg bg-surface-container-low p-8">
              <h3 className="mb-6 flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-on-surface-variant">
                <span className="material-symbols-outlined text-base text-primary">link</span>
                Dependencies
              </h3>

              <div className="space-y-4">
                {dependencyTasks.length ? (
                  dependencyTasks.map((task) => {
                    const done = ['COMPLETED', 'CLOSED'].includes(String(task.status || '').toUpperCase())
                    return (
                      <div key={task.id} className="flex items-center justify-between rounded-md border border-outline-variant/20 bg-surface-container-lowest p-4">
                        <div className="flex items-center gap-4">
                          <span className="rounded bg-surface-container-high px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-on-surface-variant">
                            {task.task_code || `T-${task.id}`}
                          </span>
                          <span className="text-sm font-semibold text-on-surface">{task.task_name || task.task_code}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`flex items-center gap-1 text-xs font-bold ${done ? 'text-on-surface-variant' : 'text-error'}`}>
                            <span className="material-symbols-outlined text-sm">{done ? 'check_circle' : 'warning'}</span>
                            {done ? 'Done' : 'Blocked'}
                          </span>
                          <button
                            type="button"
                            onClick={() => actions.removeDependencyTask(task.id)}
                            className="material-symbols-outlined text-base text-on-surface-variant hover:text-error"
                          >
                            close
                          </button>
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <div className="flex items-center justify-between rounded-md border border-outline-variant/20 bg-surface-container-lowest p-4">
                    <div className="flex items-center gap-4">
                      <span className="rounded bg-surface-container-high px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-on-surface-variant">
                        T-000
                      </span>
                      <span className="text-sm font-semibold text-on-surface-variant">No linked dependencies</span>
                    </div>
                    <span className="text-xs font-bold text-on-surface-variant">Open</span>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setIsDependencyPickerOpen(true)}
                  className="flex w-full items-center justify-center gap-2 rounded-md border-2 border-dashed border-outline-variant px-4 py-4 text-sm font-bold text-on-surface-variant transition-all hover:border-primary hover:bg-surface-container-high hover:text-primary"
                >
                  <span className="material-symbols-outlined">add_link</span>
                  Link Existing Tasks
                </button>
              </div>
            </article>
            {/* 
            <article className="rounded-[2rem] border border-outline-variant/20 bg-surface-container-low p-7">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <label className="grid gap-2">
                  <span className="text-sm font-semibold text-on-surface">Attachments (comma separated)</span>
                  <input
                    type="text"
                    value={state.form.attachmentsText}
                    onChange={(event) => actions.patchForm('attachmentsText', event.target.value)}
                    placeholder="doc.pdf, wireframe.png"
                    className="rounded-xl border-none bg-surface-container-lowest px-4 py-3 text-base text-on-surface outline-none"
                  />
                </label>

                <label className="grid gap-2">
                  <span className="text-sm font-semibold text-on-surface">Tags (comma separated)</span>
                  <input
                    type="text"
                    value={state.form.tagsText}
                    onChange={(event) => actions.patchForm('tagsText', event.target.value)}
                    placeholder="migration, security"
                    className="rounded-xl border-none bg-surface-container-lowest px-4 py-3 text-base text-on-surface outline-none"
                  />
                </label>
              </div>
            </article> */}
          </div>

          <aside className="flex h-full flex-col gap-6">
            <article className="rounded-[2rem] border border-outline-variant/20 bg-surface-container-low p-6">
              <h3 className="mb-5 text-3xl font-black tracking-tight text-on-surface">Task Metadata</h3>

              <div className={`mb-4 inline-flex rounded-xl border px-4 py-2 text-base font-bold ${statusChipClass(state.form.status)}`}>
                {state.form.status.replaceAll('_', ' ')}
              </div>

              {/* <div className="mb-4">
                <div className="mb-2 flex items-center justify-between text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                  <span>Complexity Points</span>
                  <span>{state.complexityPoints}</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={state.complexityPoints}
                  onChange={(event) => actions.setComplexityPoints(Number(event.target.value))}
                  className="w-full accent-primary"
                />
                <div className="mt-1 flex justify-between text-[11px] font-bold text-on-surface-variant">
                  <span>Low</span>
                  <span>Critical</span>
                </div>
              </div> */}

              <div className="space-y-2 border-t border-outline-variant/20 pt-4 text-base">
                <div className="flex items-center justify-between">
                  <span className="text-on-surface-variant">Estimated Effort</span>
                  <strong className="text-on-surface">
                    {Number(state.form.effort_hours || 0)}h {Number(state.form.effort_minutes || 0)}m
                  </strong>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-on-surface-variant">Risk Level</span>
                  <strong className={riskToneClass(state.riskLevel)}>{state.riskLevel}</strong>
                </div>
              </div>

              <div className="mt-4 border-t border-outline-variant/20 pt-4">
                <div className="mb-2 flex items-center justify-between text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                  <span>Completion</span>
                  <span>{state.completionPercent}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-surface-variant">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${Math.max(6, state.completionPercent)}%` }} />
                </div>
              </div>
            </article>

            <article className="rounded-[2rem] bg-primary p-6 text-on-primary">
              <h3 className="text-4xl font-black tracking-tight">Need help?</h3>
              <p className="mt-3 text-sm leading-relaxed text-primary-fixed-dim">
                AI assistant can suggest assignments and prefill descriptions from similar tasks.
              </p>
              <button type="button" className="mt-6 w-full rounded-full bg-white px-4 py-2 text-sm font-black text-primary">
                Activate BridgeAI
              </button>
            </article>

            <article className="rounded-[2rem] border border-outline-variant/20 bg-surface-container-low p-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-on-surface">Mark Completed</span>
                <AppToggle
                  checked={Boolean(state.form.is_completed)}
                  onChange={() => actions.toggleCompleted()}
                  ariaLabel="Toggle task completion"
                />
              </div>

              {Boolean(state.form.is_completed) ? (
                <label className="grid gap-2 pt-5">
                  <span className="text-sm font-semibold text-on-surface">
                    Completion Note
                    <span className="text-error"> *</span>
                  </span>
                  <input
                    type="text"
                    value={state.form.completion_note}
                    onChange={(event) => actions.patchForm('completion_note', event.target.value)}
                    placeholder="Required when status is COMPLETED/CLOSED"
                    className="rounded-xl border-none bg-surface-container-lowest px-4 py-3 text-sm text-on-surface outline-none"
                  />
                </label>
              ) : null}
            </article>

            <div className="mt-auto flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={actions.handleDiscard}
                className="rounded-lg px-5 py-3 text-base font-bold text-primary transition-colors hover:bg-surface-container-high"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={() => actions.handleSave(false)}
                disabled={state.isSaving || state.isLoading}
                className="rounded-lg bg-primary px-8 py-3 text-base font-bold text-on-primary shadow-lg shadow-primary/25 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {state.isSaving ? 'Saving...' : state.isEditMode ? 'Save Task' : 'Save Task'}
              </button>
              {!state.isEditMode && assignedUsers.length === 0 && (
                <button
                  type="button"
                  onClick={() => actions.handleSaveAndAllocate()}
                  disabled={state.isSaving || state.isLoading}
                  className="rounded-lg border border-primary bg-primary/10 px-5 py-3 text-sm font-bold text-primary transition-all hover:bg-primary/20 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Save &amp; Allocate on Board (Estimated) &rarr;
                </button>
              )}
            </div>
          </aside>
        </div>
      )}

      {isAssigneePickerOpen ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/35 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-5 shadow-2xl">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-lg font-bold text-on-surface">Select Internal Assignee</h4>
              <button type="button" onClick={() => setIsAssigneePickerOpen(false)} className="rounded-full p-1 hover:bg-surface-container-low">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="relative mb-3">
              <span className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-outline">
                search
              </span>
              <input
                type="text"
                value={assigneeSearch}
                onChange={(e) => setAssigneeSearch(e.target.value)}
                placeholder="Search assignees..."
                className="w-full rounded-md border border-outline-variant/30 bg-surface-container-lowest py-2.5 pl-10 pr-4 text-sm text-on-surface outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="rounded-md border border-outline-variant/30 bg-surface-container-lowest p-1">
              {/* Option to send to unassigned pool */}
              <button
                type="button"
                onClick={() => {
                  actions.assignToPool()
                  setIsAssigneePickerOpen(false)
                }}
                className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left transition-colors bg-primary/5 border border-dashed border-primary/30 hover:bg-primary/10 mb-1"
              >
                <div className="flex items-center gap-2.5">
                  <span className="material-symbols-outlined text-primary text-base">inbox</span>
                  <div>
                    <p className="text-sm font-bold text-on-surface">Unassigned (Send to Task Pool)</p>
                    <p className="text-[10px] text-on-surface-variant font-medium">Keep unassigned for drag-and-drop allocation on the board</p>
                  </div>
                </div>
                <span className="text-[9px] font-extrabold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                  Pool
                </span>
              </button>

              <AppScrollArea
                maxHeight="260px"
                onLoadMore={assigneeHasMore ? () => { } : undefined}
                isLoading={assigneeIsLoading}
                hasMore={assigneeHasMore}
              >
                <div className="space-y-1 pr-1">
                  {safeAssigneeOptions.length ? (
                    safeAssigneeOptions.map((user) => {
                      const selected = formAssignedToCsv.includes(Number(user.value))
                      return (
                        <button
                          key={`assignee-${user.value}`}
                          type="button"
                          onClick={() => {
                            if (selected) {
                              actions.removeAssignee(user.value)
                              return
                            }
                            actions.addAssignee(user.value)
                            setIsAssigneePickerOpen(false)
                          }}
                          className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left transition-colors ${selected ? 'bg-primary-fixed text-primary' : 'hover:bg-surface-container-low'
                            }`}
                        >
                          <div>
                            <p className="text-sm font-semibold">{user.label}</p>
                            <p className="text-xs opacity-75">{user.subLabel}</p>
                          </div>
                          <span className="text-xs">{selected ? 'Selected' : 'Select'}</span>
                        </button>
                      )
                    })
                  ) : (
                    <div className="px-3 py-4 text-center text-sm text-on-surface-variant">
                      {assigneeIsLoading ? 'Loading users...' : 'No users found.'}
                    </div>
                  )}
                  {assigneeHasMore ? <div ref={assigneeSentinelRef} className="h-1" /> : null}
                </div>
              </AppScrollArea>
            </div>
          </div>
        </div>
      ) : null}

      {isDependencyPickerOpen ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/35 p-4">
          <div className="w-full max-w-xl rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-5 shadow-2xl">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-lg font-bold text-on-surface">Link Existing Tasks</h4>
              <button type="button" onClick={() => setIsDependencyPickerOpen(false)} className="rounded-full p-1 hover:bg-surface-container-low">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="relative mb-3">
              <span className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-outline">
                search
              </span>
              <input
                type="text"
                value={depSearch}
                onChange={(e) => setDepSearch(e.target.value)}
                placeholder="Search tasks..."
                className="w-full rounded-md border border-outline-variant/30 bg-surface-container-lowest py-2.5 pl-10 pr-4 text-sm text-on-surface outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="rounded-md border border-outline-variant/30 bg-surface-container-lowest p-1">
              <AppScrollArea
                maxHeight="320px"
                onLoadMore={depHasMore ? () => { } : undefined}
                isLoading={depIsLoading}
                hasMore={depHasMore}
              >
                <div className="space-y-1 pr-1">
                  {safeDependencyTasks.length ? (
                    safeDependencyTasks
                      .filter((task) => Number(task.value) !== Number(state.editingTaskId || 0))
                      .map((task) => {
                        const selected = formDependencyTaskIds.includes(Number(task.value))
                        return (
                          <button
                            key={`dependency-${task.value}`}
                            type="button"
                            onClick={() =>
                              selected ? actions.removeDependencyTask(task.value) : actions.addDependencyTask(task.value)
                            }
                            className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left transition-colors ${selected ? 'bg-primary-fixed text-primary' : 'hover:bg-surface-container-low'
                              }`}
                          >
                            <div>
                              <p className="text-sm font-semibold">{task.label}</p>
                              <p className="text-xs opacity-75">{task.subLabel}</p>
                            </div>
                            <span className="text-xs">{selected ? 'Linked' : 'Link'}</span>
                          </button>
                        )
                      })
                  ) : (
                    <div className="px-3 py-4 text-center text-sm text-on-surface-variant">
                      {depIsLoading ? 'Loading tasks...' : 'No tasks found.'}
                    </div>
                  )}
                  {depHasMore ? <div ref={depSentinelRef} className="h-1" /> : null}
                </div>
              </AppScrollArea>
            </div>
          </div>
        </div>
      ) : null}

      {availabilityUser && (
        <AllocationAssignmentModal
          employee={{
            employee_id: availabilityUser.id,
            name: availabilityUser.name,
            base_hours_per_day: 8,
          }}
          preSelectedTask={{
            task_id: state.editingTaskId || 0,
            task_code: state.form.task_code || (state.editingTaskId ? '' : 'NEW'),
            task_name: state.form.task_name || 'New Task',
            project_name: state.initialProjectName || 'Project',
            effort_estimate: state.form.effort_estimate,
            planned_start: state.form.planned_start,
            planned_end: state.form.planned_end,
          }}
          month={getCurrentMonth()}
          isUpdate={!!state.editingTaskId}
          mode="select"
          onAssign={(payload) => {
            if (payload.planned_start) {
              actions.patchForm('planned_start', payload.planned_start.split('T')[0])
            }
            if (payload.planned_end) {
              actions.patchForm('planned_end', payload.planned_end.split('T')[0])
            }
            if (payload.estimated_hours != null) {
              actions.setEffortEstimate(payload.estimated_hours)
            }
            setAvailabilityUser(null)
          }}
          onClose={() => setAvailabilityUser(null)}
          onRefresh={() => refreshEmployee({ employee_id: availabilityUser.id })}
          isRefreshing={isRefreshingEmployee}
        />
      )}

    </div>
  )
}
